import { useEffect, useState } from 'react'
import { useAuth } from './useAuth'

export interface CurrentUser {
  name: string
  email: string
}

interface SwaClientPrincipal {
  userDetails?: string
  claims?: { typ: string; val: string }[]
}

/**
 * Module-level cache so the /.auth/me round trip happens once per page load no
 * matter how many components ask.
 */
let swaPrincipalPromise: Promise<CurrentUser | null> | null = null

function fetchSwaPrincipal(): Promise<CurrentUser | null> {
  swaPrincipalPromise ??= fetch('/.auth/me')
    .then((res) => (res.ok ? res.json() : null))
    .then((data: { clientPrincipal?: SwaClientPrincipal } | null) => {
      const principal = data?.clientPrincipal
      if (!principal) return null
      const claimName = principal.claims?.find((c) => c.typ === 'name' || c.typ.endsWith('/name'))?.val
      const email = principal.userDetails ?? ''
      return { name: claimName ?? email ?? 'User', email }
    })
    .catch(() => null)
  return swaPrincipalPromise
}

/**
 * The signed-in user, from whichever auth path is actually in play.
 *
 * `useAuth()` derives its user purely from MSAL accounts, so on the Static Web
 * Apps path — which is what production uses — it returns null and the name,
 * avatar and sign-out control silently disappear. The identity is available
 * there (`/.auth/me` returns a clientPrincipal); it was simply being discarded
 * after the authenticated check.
 *
 * This prefers the MSAL account when there is one and falls back to the SWA
 * principal otherwise, so the shell's Administration block works on both paths.
 * Deliberately separate from `useAuth` to keep the token-acquisition logic in
 * that hook untouched.
 */
export function useCurrentUser(): CurrentUser | null {
  const { user: msalUser, isEntraEnabled } = useAuth()
  const [swaUser, setSwaUser] = useState<CurrentUser | null>(null)

  useEffect(() => {
    // Only reach for the SWA principal when MSAL cannot supply one.
    if (msalUser || isEntraEnabled) return
    let active = true
    void fetchSwaPrincipal().then((u) => { if (active) setSwaUser(u) })
    return () => { active = false }
  }, [msalUser, isEntraEnabled])

  return msalUser ?? swaUser
}

/**
 * Sign-out that works on both paths: MSAL redirect when Entra is configured,
 * otherwise the Static Web Apps logout endpoint.
 */
export function useSignOut(): () => void {
  const { logout, isEntraEnabled } = useAuth()
  return () => {
    if (isEntraEnabled) {
      void logout()
      return
    }
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/login'
  }
}
